import {
  MultiSelect,
  type MultiSelectProps,
} from '@progress/kendo-react-dropdowns'
import classNames from 'classnames'
import React from 'react'

import { Typography } from '~/components/elements/Typography'

import styles from './ItemMultiSelector.css'

const tagRender: MultiSelectProps['tagRender'] = (tagData, li) =>
  React.cloneElement(li, li.props, [
    <Typography key="text" variant="normal">
      {tagData.text}
    </Typography>,
    li.props.children,
  ])

export type ItemMultiSelectorProps = Pick<
  MultiSelectProps,
  'className' | 'dataItemKey' | 'disabled' | 'onBlur' | 'value'
> & {
  'data-testid'?: string
  data: NonNullable<MultiSelectProps['data']> | null
  textField: NonNullable<MultiSelectProps['textField']>
  onChange?: (value: NonNullable<MultiSelectProps['value']>) => void
}

export const ItemMultiSelector = React.forwardRef<
  HTMLDivElement,
  ItemMultiSelectorProps
>(
  (
    {
      className,
      data,
      'data-testid': dataTestId = 'components-elements-item-multi-selector',
      onChange,
      textField,
      ...rest
    },
    ref,
  ) => {
    const itemRender = React.useMemo<MultiSelectProps['itemRender']>(
      () => (li, itemProps) => {
        const text = itemProps.dataItem[textField]

        return React.cloneElement(
          li,
          li.props,
          <Typography color="inherit" variant="normal">
            {text}
          </Typography>,
        )
      },
      [textField],
    )

    const handleChange = React.useCallback<EH<MultiSelectProps, 'onChange'>>(
      (event) => {
        onChange?.(event.value)
      },
      [onChange],
    )

    return (
      <MultiSelect
        ref={ref}
        className={classNames(styles.root, className)}
        data={data ?? undefined}
        data-testid={dataTestId}
        itemRender={itemRender}
        tagRender={tagRender}
        textField={textField}
        onChange={handleChange}
        {...rest}
      />
    )
  },
)
